import { SnackyPop } from "../../components/Snackbar";
import { ApiRoutes } from "./routes";
import { Servers } from "./server";

enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
    OPTION = 'OPTION'
}


export interface IHttpRequest<TBody = any> {
    route: ApiRoutes | string;
    uriParams?: any;
    body?: TBody;
    headers?: HeadersInit;
    handleErr?: boolean;
}


export interface IResponse<T> {
    /**Status of the Reponse - True/False */
    status: boolean;
    /** Response data */
    data?: T;
    /** Message if anything needs to be sent - Can be sent when status is either True/False */
    message?: string;
    /** Error details to be sent to client - Can be sent when status is False */
    error?: string;
}

export interface IAppException {
    message?: string | undefined;
    statusCode?: number | undefined;
    error?: Error | undefined;
}

export class HttpClient {

    public static baseUrl = Servers.API;

    private static addRequestHeaders(headers?: Record<string, string> | any): HeadersInit {
        headers = {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": true,
            "TenantId": Servers.TENANTID,
            ...headers,
        };

        this.addAuthHeader(headers);

        return headers;
    }

    private static initRequest(request: IHttpRequest) {
        request.handleErr = request.handleErr ?? true;
    }

    private static addAuthHeader(headers: Record<string, string>): HeadersInit {
        headers.Authorization = localStorage.getItem("token") || "";
        return headers;
    }

    public static async get<TResponse>(request: IHttpRequest): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addRequestHeaders(request.headers);
        let response = await fetch(this.formURL(request), {
            method: HttpMethod.GET,
            headers: headers
        }).catch(ex => {
            alert(ex)
            // if (!request.handleErr)
            //     throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    public static async post<TResponse, TBody>(request: IHttpRequest<TBody>): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addRequestHeaders(request.headers);

        let response = await fetch(this.formURL(request), {
            method: HttpMethod.POST,
            headers: headers,
            body: JSON.stringify(request.body),
        }).catch(ex => {
            if (!request.handleErr)
                throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    public static async patch<TResponse, TBody>(request: IHttpRequest<TBody>): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addRequestHeaders(request.headers);
        let response = await fetch(this.formURL(request), {
            method: HttpMethod.PATCH,
            headers: headers,
            body: JSON.stringify(request.body),
        }).catch(ex => {
            if (!request.handleErr)
                throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    public static async put<TResponse, TBody>(request: IHttpRequest<TBody>): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addRequestHeaders(request.headers);
        let response = await fetch(this.formURL(request), {
            method: HttpMethod.PUT,
            headers: headers,
            body: JSON.stringify(request.body)
        }).catch(ex => {
            if (!request.handleErr)
                throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    public static async delete<TResponse, TBody>(request: IHttpRequest<TBody>): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addRequestHeaders(request.headers);
        let response = await fetch(this.formURL(request), {
            method: HttpMethod.DELETE,
            headers: headers,
            body: JSON.stringify(request.body)
        }).catch(ex => {
            if (!request.handleErr)
                throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    public static async uploadFile<TResponse>(request: IHttpRequest<FormData>): Promise<IResponse<TResponse>> {
        this.initRequest(request);
        let headers = this.addAuthHeader((request.headers || {}) as any);

        let response = await fetch(this.formURL(request), {
            method: HttpMethod.POST,
            headers: headers,
            body: request.body,
        }).catch(ex => {
            if (!request.handleErr)
                throw ex
        });

        if (!response) return {} as IResponse<TResponse>;

        return await this.returnResponse(request, response);
    }

    private static async returnResponse<TResponse>(request: IHttpRequest, response: Response): Promise<IResponse<TResponse>> {
        let result = await response.json() as IResponse<TResponse>;
        if (!result.status) {
            if (request.handleErr) {
                SnackyPop.showSnack({ message: result.message || result.error || 'Error while processing your request.' });
                throw result;
            }
            else
                throw result;
        }
        return result;
    }

    public static handleNetworkError() {

    }

    private static onError() {

    }



    //#region Private methods

    private static getBody(req: IHttpRequest, headers: HeadersInit): any {
        if (this.isContentJson(headers))
            return req.body ? JSON.stringify(req.body) : undefined;
        else
            return req.body;
    }

    private static isContentJson(headers: HeadersInit) {
        return false;
    }

    private static formURL(req: IHttpRequest): string {
        this.validate(req);
        let url: string = `${this.baseUrl}`;
        if (req.uriParams != undefined && req.uriParams != null && typeof req.uriParams == 'object') {
            let route: string = req.route;
            let keys: string[] = Object.keys(req.uriParams);
            for (let i = 0; i < keys.length; i++) {
                var regEx = new RegExp('{' + keys[i] + '}', 'ig');
                route = route.replace(regEx, req.uriParams[keys[i]]);
            }
            url += route;
        } else {
            url += req.route;
        }

        url = this.formatQueryString(url, req);
        return url;
    }


    /**
    * Query String Implementation
    *
    * Input
    *
    * url = http://localhost/route?{id:QueryString}&{name:QueryString}
    * UriParams = {
    * id: [1,2,3]
    * name: 'Bob'
    * }
    *
    * output
    *
    * url = http://localhost/route?id=1&id=2&id=3&name=bob
    *
    */
    private static formatQueryString(url: string, req: IHttpRequest): string {
        let queryParams: string[] = this.matchQueryStringRegex(url);
        for (var queryParam of queryParams) {
            var paramName = queryParam.split(':')[0];
            paramName = paramName ? paramName : '';
            paramName = paramName.replace('{', '');
            var qParamValue = req.uriParams[paramName];
            var qParamString = '';
            if (typeof qParamValue == 'object' && qParamValue && qParamValue.length > 0) {
                for (var value of qParamValue) {
                    qParamString += `${paramName}=${value}&`
                }
                // To remove last &
                qParamString = qParamString.substring(0, qParamString.length - 1);
            }
            else if (typeof qParamValue == 'object' && qParamValue && qParamValue.length == undefined) {
                let keys: string[] = Object.keys(qParamValue);
                for (let i = 0; i < keys.length; i++) {
                    let route = `${keys[i]}=${qParamValue[keys[i]]}&`;
                    qParamString += route;
                }

                // To remove last &
                if (qParamString.lastIndexOf('&') == qParamString.length - 1)
                    qParamString = qParamString.substring(0, qParamString.length - 1);
            }
            else {
                qParamString = `${paramName}=${qParamValue}`;
            }
            url = url.replace(queryParam, qParamString);
        }
        return url;
    }

    private static matchQueryStringRegex(url: string): string[] {
        var regex = /{[A-Z]+:QueryString}/gi;
        var expMatch: RegExpExecArray | null;
        var result: string[] = [];
        while ((expMatch = regex.exec(url)) !== null) {
            // This is necessary to avoid infinite loops with zero-width matches
            if (expMatch.index === regex.lastIndex) {
                regex.lastIndex++;
            }

            // The result can be accessed through the `m`-variable.
            expMatch.forEach((match, groupIndex) => {
                result.push(match);
            });
        }
        return result;
    }

    private static validate(req: IHttpRequest): void {
        if (!req) {
            throw new Error('Route not defined');
        }
        if (req.route.includes('{') && !req.uriParams) {
            let parameters = req.route.match('\{(.*?)\}');
            let message: string = `Route Param '${parameters && parameters[1]}' is not defined in route '${req.route}'`;
            alert(message);
            throw new Error(message);
        }
    }

    //#endregion
}